import { type ToolCallStatus } from '@kapaai/agent-react';
import {
  Box,
  Flex,
  Group,
  Loader,
  Stack,
  Text,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import { IconCheck, IconHeadset, IconX } from '@tabler/icons-react';

export interface NotifyKapaCardResult {
  issue_number?: number;
  title?: string;
}

interface NotifyKapaCardProps {
  status: ToolCallStatus;
  title: string;
  result?: NotifyKapaCardResult;
  onApprove?: () => void;
  onReject?: () => void;
}

export const NotifyKapaCard = ({
  status,
  title,
  result,
  onApprove,
  onReject,
}: NotifyKapaCardProps) => {
  const theme = useMantineTheme();
  const isAwaitingApproval = status === 'approval_requested';
  const isExecuting = status === 'executing';
  const isCompleted = status === 'completed';
  const isError = status === 'error';
  const isDenied = status === 'denied';
  const isStopped = status === 'stopped';

  return (
    <Box
      bg="var(--color-surface-elevated)"
      sx={() => ({
        border: `1px solid ${
          isAwaitingApproval ? 'var(--color-line-strong)' : 'var(--color-line)'
        }`,
        overflow: 'hidden',
      })}
    >
      {/* Header bar */}
      <Group
        wrap="nowrap"
        gap="xs"
        bg="var(--color-surface-secondary)"
        px="xs"
        py={5}
      >
        <Flex
          align="center"
          justify="center"
          w={16}
          h={16}
          sx={{ flexShrink: 0 }}
        >
          <IconHeadset size={16} stroke={1.5} color="var(--color-foreground)" />
        </Flex>

        <Text
          size="xs"
          fw={500}
          c="var(--color-foreground-secondary)"
          ta="left"
          flex={1}
        >
          Notify Kapa Support
        </Text>

        <Group gap={6} wrap="nowrap">
          {isAwaitingApproval && (
            <Text
              component="span"
              size="xs"
              fw={600}
              c="var(--color-foreground)"
              lh={1.4}
              px={8}
              py={2}
              sx={() => ({
                backgroundColor: 'var(--color-surface-secondary)',
                borderRadius: theme.radius.sm,
              })}
            >
              Approval required
            </Text>
          )}

          {isDenied && (
            <Text
              component="span"
              size="xs"
              fw={600}
              c="var(--color-intent-destructive)"
              lh={1.4}
              px={8}
              py={2}
              sx={() => ({
                backgroundColor:
                  'color-mix(in oklab, var(--color-intent-destructive) 10%, transparent)',
                borderRadius: theme.radius.sm,
              })}
            >
              Dismissed
            </Text>
          )}

          {isStopped && (
            <Text
              component="span"
              size="xs"
              fw={500}
              c="var(--color-foreground-tertiary)"
            >
              Stopped
            </Text>
          )}

          {isExecuting && (
            <Loader
              size={12}
              type="dots"
              color="var(--color-foreground-tertiary)"
            />
          )}

          {isCompleted && (
            <IconCheck size={14} color="var(--color-intent-success)" />
          )}

          {isError && (
            <IconX size={14} color="var(--color-intent-destructive)" />
          )}
        </Group>
      </Group>

      {/* Content */}
      <Stack
        gap="xs"
        p="sm"
        bg="var(--color-surface-elevated)"
        sx={{ borderTop: '1px solid var(--color-line-strong)' }}
      >
        <Text size="xs" c="var(--color-foreground-secondary)">
          {title}
        </Text>

        {isAwaitingApproval && (
          <Group gap="xs">
            <UnstyledButton
              onClick={onApprove}
              bg="var(--color-surface-inverse)"
              c="var(--color-foreground-inverse)"
              fw={500}
              fz="xs"
              px={12}
              py={4}
              sx={{ borderRadius: theme.radius.sm }}
            >
              Notify Kapa Team
            </UnstyledButton>
            <UnstyledButton
              onClick={onReject}
              c="var(--color-foreground-tertiary)"
              fw={500}
              fz="xs"
              px={12}
              py={4}
              sx={{ borderRadius: theme.radius.sm }}
            >
              Dismiss
            </UnstyledButton>
          </Group>
        )}

        {isCompleted && result && (
          <Group gap="xs">
            <IconCheck size={14} color="var(--color-intent-success)" />
            <Text size="xs" c="var(--color-foreground-secondary)">
              Ticket created
              {result.issue_number ? ` (#${result.issue_number})` : ''}
            </Text>
          </Group>
        )}

        {isError && (
          <Text size="xs" c="var(--color-intent-destructive)">
            Could not create the support ticket. Please try again.
          </Text>
        )}
      </Stack>
    </Box>
  );
};
